import { useState } from "react";
import { Bus, Loader2, Pencil, Plus, Trash2, TrainFront, TramFront, ArrowDownUp, Footprints, HelpCircle } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { CROSSING_TYPES, LANDMARK_TYPES, SEGMENT_TYPES } from "@/config/journeyCatalog";

const SEGMENT_ICONS = {
  walk: Footprints, bus: Bus, train: TrainFront, underground: TrainFront,
  tram: TramFront, transfer: ArrowDownUp, other: HelpCircle,
};

const EMPTY_SEGMENT = {
  id: "", type: "walk", start_point: "", end_point: "", description: "",
  estimated_duration: "", distance: "", instructions: "", notes: "",
  route_number: "", operator: "", boarding_stop: "", alighting_stop: "", direction_dest: "",
  num_stops: "", interchange_info: "", accessibility_info: "", travel_card: "", known_issues: "",
  important_landmarks: "", road_crossings: "", pedestrian_crossings: "", traffic_considerations: "",
  known_hazards: "", landmarks: [], crossings: [],
};

function newId() {
  return Math.random().toString(36).slice(2, 10);
}

function LandmarkRows({ items, onChange, childId, readOnly }) {
  const set = (i, k, v) => onChange(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const uploadPhoto = async (i, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", "journey_photo");
    fd.append("child_id", childId);
    fd.append("description", `Landmark: ${items[i].name || "journey photo"}`);
    try {
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(i, "photo_document_id", data.id);
      toast.success("Photo uploaded securely");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <div className="space-y-2">
      {items.map((l, i) => (
        <div key={l.id || i} className="rounded-md border border-border p-3 space-y-2" data-testid={`landmark-row-${i}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Select value={l.type || ""} disabled={readOnly} onValueChange={(v) => set(i, "type", v)}>
              <SelectTrigger data-testid={`landmark-type-${i}`} className="h-10"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent className="bg-white">
                {LANDMARK_TYPES.map((t) => <SelectItem key={t} value={t.toLowerCase().replace(/\s+/g, "_")}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Name (e.g. Tesco)" value={l.name || ""} disabled={readOnly} data-testid={`landmark-name-${i}`} onChange={(e) => set(i, "name", e.target.value)} />
          </div>
          <Input placeholder="Why it is important" value={l.importance || ""} disabled={readOnly} data-testid={`landmark-importance-${i}`} onChange={(e) => set(i, "importance", e.target.value)} />
          <Textarea rows={2} placeholder='Instruction to child — e.g. "When you see Tesco on your left, prepare to get off at the next stop."' value={l.instruction || ""} disabled={readOnly} data-testid={`landmark-instruction-${i}`} onChange={(e) => set(i, "instruction", e.target.value)} />
          <div className="flex items-center justify-between gap-2">
            {!readOnly && (
              <label className="text-xs text-primary font-medium cursor-pointer hover:underline" data-testid={`landmark-photo-${i}`}>
                {l.photo_document_id ? "Replace photo ✓" : "Add photo"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(i, e.target.files?.[0])} />
              </label>
            )}
            {readOnly && l.photo_document_id && (
              <a className="text-xs text-primary hover:underline" target="_blank" rel="noreferrer"
                href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${l.photo_document_id}/download`}>View photo</a>
            )}
            {!readOnly && (
              <Button type="button" variant="ghost" className="h-9 text-red-600 ml-auto" data-testid={`landmark-remove-${i}`}
                onClick={() => onChange(items.filter((_, j) => j !== i))}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      ))}
      {!readOnly && (
        <Button type="button" variant="outline" className="h-10" data-testid="landmark-add-button"
          onClick={() => onChange([...items, { id: newId(), type: "", name: "", importance: "", instruction: "", photo_document_id: null }])}>
          <Plus className="h-4 w-4 mr-2" /> Add landmark
        </Button>
      )}
    </div>
  );
}

function CrossingRows({ items, onChange, readOnly }) {
  const set = (i, k, v) => onChange(items.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-2">
      {items.map((c, i) => (
        <div key={c.id || i} className="rounded-md border border-border p-3 space-y-2" data-testid={`crossing-row-${i}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Select value={c.crossing_type || ""} disabled={readOnly} onValueChange={(v) => set(i, "crossing_type", v)}>
              <SelectTrigger data-testid={`crossing-type-${i}`} className="h-10"><SelectValue placeholder="Crossing type" /></SelectTrigger>
              <SelectContent className="bg-white">
                {CROSSING_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Location / description" value={c.location || ""} disabled={readOnly} data-testid={`crossing-location-${i}`} onChange={(e) => set(i, "location", e.target.value)} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input placeholder="Traffic conditions" value={c.traffic_conditions || ""} disabled={readOnly} onChange={(e) => set(i, "traffic_conditions", e.target.value)} />
            <Input placeholder="Visibility" value={c.visibility || ""} disabled={readOnly} onChange={(e) => set(i, "visibility", e.target.value)} />
          </div>
          <Textarea rows={2} placeholder="Identified hazard" value={c.hazard || ""} disabled={readOnly} data-testid={`crossing-hazard-${i}`} onChange={(e) => set(i, "hazard", e.target.value)} />
          <Textarea rows={2} placeholder="Required strategy" value={c.strategy || ""} disabled={readOnly} data-testid={`crossing-strategy-${i}`} onChange={(e) => set(i, "strategy", e.target.value)} />
          <Textarea rows={2} placeholder="Trainer instruction" value={c.trainer_instruction || ""} disabled={readOnly} data-testid={`crossing-instruction-${i}`} onChange={(e) => set(i, "trainer_instruction", e.target.value)} />
          <p className="text-[11px] text-slate-500">A crossing type never means a crossing is automatically safe — record the observed strategy.</p>
          {!readOnly && (
            <Button type="button" variant="ghost" className="h-9 text-red-600" data-testid={`crossing-remove-${i}`}
              onClick={() => onChange(items.filter((_, j) => j !== i))}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ))}
      {!readOnly && (
        <Button type="button" variant="outline" className="h-10" data-testid="crossing-add-button"
          onClick={() => onChange([...items, { id: newId(), crossing_type: "", location: "", traffic_conditions: "", visibility: "", hazard: "", strategy: "", trainer_instruction: "" }])}>
          <Plus className="h-4 w-4 mr-2" /> Add road crossing
        </Button>
      )}
    </div>
  );
}

export default function SegmentsEditor({ directionKey, label, segments, onChange, childId, readOnly }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [form, setForm] = useState(EMPTY_SEGMENT);
  const [saving, setSaving] = useState(false);

  const openAdd = () => {
    setEditingIdx(null);
    setForm({ ...EMPTY_SEGMENT, id: newId() });
    setDialogOpen(true);
  };

  const openEdit = (i) => {
    setEditingIdx(i);
    setForm({ ...EMPTY_SEGMENT, ...segments[i] });
    setDialogOpen(true);
  };

  const saveSegment = () => {
    let next;
    if (editingIdx === null) {
      next = [...segments, { ...form, order: segments.length + 1 }];
    } else {
      next = segments.map((s, i) => (i === editingIdx ? { ...form, order: s.order } : s));
    }
    onChange(next);
    setDialogOpen(false);
  };

  const removeSegment = (i) => onChange(segments.filter((_, j) => j !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= segments.length) return;
    const next = [...segments];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next.map((s, k) => ({ ...s, order: k + 1 })));
  };

  const isTransport = ["bus", "train", "underground", "tram"].includes(form.type);

  return (
    <div className="space-y-3" data-testid={`segments-${directionKey}`}>
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold text-slate-900">{label}</h3>
        {!readOnly && (
          <Button type="button" variant="outline" className="h-10" data-testid={`segment-add-${directionKey}`} onClick={openAdd}>
            <Plus className="h-4 w-4 mr-2" /> Add segment
          </Button>
        )}
      </div>

      <div className="rounded-md border border-border bg-slate-50 px-3 py-2 text-center text-sm font-bold text-slate-700" data-testid={`segments-start-${directionKey}`}>
        {directionKey === "outbound" ? "HOME" : "SCHOOL"}
      </div>

      {segments.length === 0 && (
        <p className="text-sm text-slate-500 text-center py-4" data-testid={`segments-empty-${directionKey}`}>No segments yet — build the route step by step.</p>
      )}

      {segments.map((s, i) => {
        const Icon = SEGMENT_ICONS[s.type] || HelpCircle;
        return (
          <div key={s.id || i} data-testid={`segment-${directionKey}-${i}`}>
            <div className="flex justify-center text-slate-400 text-lg leading-none">↓</div>
            <div className="rounded-md border border-border bg-white p-3">
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-light">
                  <Icon className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">
                    {SEGMENT_TYPES.find((t) => t.value === s.type)?.label || s.type}
                    {s.route_number ? ` ${s.route_number}` : ""}
                  </p>
                  <p className="text-xs text-slate-600">
                    {s.type === "walk" || s.type === "transfer" || s.type === "other"
                      ? `${s.start_point || "?"} → ${s.end_point || "?"}`
                      : `Board ${s.boarding_stop || "?"} → get off ${s.alighting_stop || "?"}`}
                    {s.estimated_duration ? ` · ~${s.estimated_duration}` : ""}
                  </p>
                  {s.instructions && <p className="text-xs text-slate-600 mt-1">{s.instructions}</p>}
                  <div className="flex gap-1.5 mt-1.5">
                    {(s.landmarks || []).length > 0 && <Badge variant="secondary" className="text-[10px]">{(s.landmarks || []).length} landmarks</Badge>}
                    {(s.crossings || []).length > 0 && <Badge variant="secondary" className="text-[10px]">{(s.crossings || []).length} crossings</Badge>}
                  </div>
                </div>
                {!readOnly && (
                  <div className="flex flex-col gap-1">
                    <Button type="button" variant="ghost" className="h-8 px-2 text-xs" data-testid={`segment-edit-${directionKey}-${i}`} onClick={() => openEdit(i)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button type="button" variant="ghost" className="h-8 px-2 text-xs" data-testid={`segment-up-${directionKey}-${i}`} onClick={() => move(i, -1)}>↑</Button>
                    <Button type="button" variant="ghost" className="h-8 px-2 text-xs" data-testid={`segment-down-${directionKey}-${i}`} onClick={() => move(i, 1)}>↓</Button>
                    <Button type="button" variant="ghost" className="h-8 px-2 text-red-600" data-testid={`segment-remove-${directionKey}-${i}`} onClick={() => removeSegment(i)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      <div className="rounded-md border border-border bg-slate-50 px-3 py-2 text-center text-sm font-bold text-slate-700" data-testid={`segments-end-${directionKey}`}>
        {directionKey === "outbound" ? "SCHOOL" : "HOME"}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingIdx === null ? "Add segment" : "Edit segment"}</DialogTitle>
            <DialogDescription>Each segment is independently editable.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4" data-testid="segment-form">
            <div className="space-y-1.5">
              <Label>Segment type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="segment-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {SEGMENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`segment-type-${t.value}`}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>{isTransport ? "Boarding stop/station" : "Start point"}</Label>
                <Input data-testid="segment-start" value={isTransport ? form.boarding_stop : form.start_point}
                  onChange={(e) => setForm({ ...form, [isTransport ? "boarding_stop" : "start_point"]: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>{isTransport ? "Alighting stop/station" : "End point"}</Label>
                <Input data-testid="segment-end" value={isTransport ? form.alighting_stop : form.end_point}
                  onChange={(e) => setForm({ ...form, [isTransport ? "alighting_stop" : "end_point"]: e.target.value })} /></div>
            </div>

            {isTransport && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Route / service number</Label>
                  <Input data-testid="segment-route-number" value={form.route_number} onChange={(e) => setForm({ ...form, route_number: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Operator</Label>
                  <Input value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Direction / destination</Label>
                  <Input data-testid="segment-direction" value={form.direction_dest} onChange={(e) => setForm({ ...form, direction_dest: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Number of stops</Label>
                  <Input value={form.num_stops} onChange={(e) => setForm({ ...form, num_stops: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Interchange information</Label>
                  <Input value={form.interchange_info} onChange={(e) => setForm({ ...form, interchange_info: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Accessibility information</Label>
                  <Input value={form.accessibility_info} onChange={(e) => setForm({ ...form, accessibility_info: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Travel card / Oyster requirements</Label>
                  <Input value={form.travel_card} onChange={(e) => setForm({ ...form, travel_card: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Known issues</Label>
                  <Input value={form.known_issues} onChange={(e) => setForm({ ...form, known_issues: e.target.value })} /></div>
              </div>
            )}

            {form.type === "walk" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Approximate distance</Label>
                  <Input value={form.distance} onChange={(e) => setForm({ ...form, distance: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Traffic considerations</Label>
                  <Input value={form.traffic_considerations} onChange={(e) => setForm({ ...form, traffic_considerations: e.target.value })} /></div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Estimated duration</Label>
                <Input data-testid="segment-duration" placeholder="e.g. 8 minutes" value={form.estimated_duration} onChange={(e) => setForm({ ...form, estimated_duration: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Description</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Instructions (clear, child-friendly wording)</Label>
              <Textarea rows={2} data-testid="segment-instructions" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Known hazards</Label>
              <Textarea rows={2} data-testid="segment-hazards" value={form.known_hazards} onChange={(e) => setForm({ ...form, known_hazards: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Notes</Label>
              <Textarea rows={1} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

            <div className="space-y-2">
              <Label className="font-semibold">Landmarks</Label>
              <LandmarkRows items={form.landmarks} onChange={(v) => setForm({ ...form, landmarks: v })} childId={childId} readOnly={false} />
            </div>
            <div className="space-y-2">
              <Label className="font-semibold">Road crossings</Label>
              <CrossingRows items={form.crossings} onChange={(v) => setForm({ ...form, crossings: v })} readOnly={false} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" data-testid="segment-save-button" disabled={saving} onClick={saveSegment}
              className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save segment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
